import * as React from "react"
import { Check, ChevronDown } from "lucide-react"

import { cn } from "@/lib/utils"
import { SELECT_TRIGGER_BASE_CLASS } from "@/components/ui/select"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { usePortalledListScroll } from "@/hooks/shared/usePortalledListScroll"

export interface SearchableSelectOption {
  value: string
  label: string
  disabled?: boolean
  /** Termos extras para busca (cmdk). */
  keywords?: string[]
  /**
   * Adorno à esquerda do rótulo — avatar, cor, bandeira. Aparece na lista E no trigger
   * fechado, senão a opção escolhida perderia justamente o traço que a fazia reconhecível.
   *
   * ⚠️ A busca do cmdk é sobre `label`/`keywords`, nunca sobre isto. Ícone é reforço visual
   * de algo que já está escrito — informação que só exista aqui fica invisível para quem
   * digita e para leitor de tela.
   */
  icon?: React.ReactNode
  /**
   * Rótulo curto no gatilho fechado; a lista continua mostrando `label`. Serve a filtros de
   * barra estreita: "Todos os responsáveis" na lista, "Responsável" no gatilho.
   */
  triggerLabel?: string
}

interface SearchableSelectOptionGroup {
  label: string
  options: SearchableSelectOption[]
}

export interface SearchableSelectProps {
  value?: string
  onValueChange: (value: string) => void
  options?: SearchableSelectOption[]
  groups?: SearchableSelectOptionGroup[]
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  disabled?: boolean
  className?: string
  triggerClassName?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Conteúdo fixo renderizado abaixo da lista, dentro do popover (ex.: ações). */
  footer?: React.ReactNode
}

export function SearchableSelect({
  value,
  onValueChange,
  options,
  groups,
  placeholder = "Selecionar...",
  searchPlaceholder = "Buscar...",
  emptyMessage = "Nenhuma opção encontrada.",
  disabled,
  className,
  triggerClassName,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  footer,
}: SearchableSelectProps) {
  const [internalOpen, setInternalOpen] = React.useState(false)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  const setOpen = (val: boolean) => {
    if (isControlled) {
      controlledOnOpenChange?.(val)
    } else {
      setInternalOpen(val)
    }
  }

  // Sem isso, a lista não rola quando o SearchableSelect é aberto de dentro de
  // um Dialog/Sheet modal — ver o comentário do hook.
  const setListRef = usePortalledListScroll()

  const allOptions = groups
    ? groups.flatMap((g) => g.options)
    : (options ?? [])
  const selectedOption = allOptions.find((opt) => opt.value === value)

  const renderOption = (option: SearchableSelectOption, reactKey?: string) => (
    <CommandItem
      key={reactKey ?? option.value}
      value={option.label}
      disabled={option.disabled}
      keywords={option.keywords}
      onSelect={() => {
        onValueChange(option.value)
        setOpen(false)
      }}
    >
      <Check
        className={cn(
          "mr-2 h-4 w-4 shrink-0",
          value === option.value ? "opacity-100" : "opacity-0"
        )}
      />
      {option.icon ? <span className="mr-2 flex shrink-0 items-center">{option.icon}</span> : null}
      <span className="min-w-0 truncate">{option.label}</span>
    </CommandItem>
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            SELECT_TRIGGER_BASE_CLASS,
            "gap-1.5 text-left font-normal",
            !selectedOption && "text-muted-foreground",
            triggerClassName
          )}
        >
          {selectedOption?.icon ? (
            <span className="flex shrink-0 items-center">{selectedOption.icon}</span>
          ) : null}
          <span className="line-clamp-1 min-w-0 flex-1 text-left">
            {selectedOption ? (selectedOption.triggerLabel ?? selectedOption.label) : placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          "flex max-h-[min(75vh,400px)] max-w-[min(100vw-1rem,420px)] flex-col overflow-hidden p-0",
          className
        )}
        style={{ minWidth: "var(--radix-popover-trigger-width)" }}
        align="start"
      >
        <Command className="min-h-0 flex-1">
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList ref={setListRef} className="max-h-[min(55vh,300px)]">
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            {groups ? (
              groups.map((group, i) => (
                <React.Fragment key={group.label}>
                  {i > 0 && <CommandSeparator />}
                  <CommandGroup heading={group.label}>
                    {group.options.map((opt) =>
                      renderOption(opt, `${group.label}:${opt.value}`)
                    )}
                  </CommandGroup>
                </React.Fragment>
              ))
            ) : (
              <CommandGroup>
                {(options ?? []).map((opt) => renderOption(opt))}
              </CommandGroup>
            )}
          </CommandList>
          {footer ? <div className="border-t p-1">{footer}</div> : null}
        </Command>
      </PopoverContent>
    </Popover>
  )
}
